import { useState, useEffect, useRef } from "react";
import { Gift } from "lucide-react";
import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import { useParams } from "react-router-dom";
import { authService } from "../../../services/api";

import { useNotification } from "../../../components/Notifications/NotificationContext";
import { useTelegramValidation } from "../../../hooks/useTelegramValidation";
import { validateGroupIds, formatGroupId } from "../../../utils/groupIdValidator";
import { MediaManager } from '../../../components/MediaManager/MediaManager';
import { apiPlansToForm } from "../../../utils/apiPlansToForm";
import { useSyncBotSelection } from "../../../hooks/useSyncBotSelection";
import { useBotCurrency } from "../../../hooks/useBotCurrency";
import TextForm from '../../../components/TextForm/TextForm';
import { validateFields } from "../../../utils/contentBlacklistValidator";

export default function OrderBump() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();
    const { currencyLabel, currencySymbol } = useBotCurrency(botId);
    const mediaRef = useRef();

    const [offerId, setOfferId] = useState(null);
    const [offerName, setOfferName] = useState("");
    const [message, setMessage] = useState("");
    const [deliverableMessage, setDeliverableMessage] = useState("");
    const [acceptText, setAcceptText] = useState("");
    const [declineText, setDeclineText] = useState("");
    const [price, setPrice] = useState(0);
    const [isActive, setIsActive] = useState(false);

    const [acceptButtonColor, setAcceptButtonColor] = useState('default');
    const [declineButtonColor, setDeclineButtonColor] = useState('default');

    const [loadingPage, setLoadingPage] = useState(true);
    const [loading, setLoading] = useState(false);

    const [mediaList, setMediaList] = useState([]);
    const [sendMode, setSendMode] = useState('agrupado');

    useSyncBotSelection();
    useEffect(() => {
        if (!botId) return;
        loadOrderBump();
    }, [botId]);

    const loadOrderBump = async () => {
        try {
            setLoadingPage(true);
            const { data } = await authService.order_bump.get_default_order_bump(botId);

            if (!data.success) return;

            const ob = data.order_bump;

            setOfferId(ob.id || null);
            setOfferName(ob.name || "");
            setMessage(ob.message || "");
            setDeliverableMessage(ob.deliverable_message || "");
            setAcceptText(ob.accept_button_text || "");
            setDeclineText(ob.decline_button_text || "");

            setAcceptButtonColor(ob.accept_button_color || 'default');
            setDeclineButtonColor(ob.decline_button_color || 'default');

            setPrice(ob.price);
            setIsActive(ob.is_active || false);
            setSendMode(ob.media_send_mode || 'agrupado');

            // Carrega lista de mídias (com fallback para campos legados)
            if (ob.media_list && ob.media_list.length > 0) {
                setMediaList(ob.media_list);
            } else {
                const legacy = [];
                if (ob.image_file_id) legacy.push({ media_type: 'photo', file_id: ob.image_file_id, thumbnail_file_id: null });
                if (ob.video_file_id) legacy.push({ media_type: 'video', file_id: ob.video_file_id, thumbnail_file_id: null });
                if (ob.audio_file_id) legacy.push({ media_type: 'voice', file_id: ob.audio_file_id, thumbnail_file_id: null });
                setMediaList(legacy);
            }

        } catch (err) {
            showError("Erro ao carregar Order Bump");
        }
        finally {
            setLoadingPage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {


            // Quando ATIVO, todos os campos são obrigatórios (o Telegram recusa
            // texto vazio) e o preço deve ser um número >= 2. validateFields só
            // checa blacklist de conteúdo — não pega campo vazio.
            if (isActive) {
                const required = [
                    ['Nome da Oferta', offerName],
                    ['Mensagem do Order Bump', message],
                    ['Mensagem do Entregável', deliverableMessage],
                    ['Texto do botão aceitar', acceptText],
                    ['Texto do botão recusar', declineText],
                ];
                for (const [label, value] of required) {
                    if (!String(value ?? '').trim()) {
                        showError(`Preencha "${label}".`);
                        setLoading(false);
                        return;
                    }
                }
                const p = Number(price);
                if (!Number.isFinite(p) || p < 2) {
                    showError('O preço deve ser um número maior ou igual a 2.');
                    setLoading(false);
                    return;
                }
            }

            const contentCheck = validateFields([
                { label: 'Nome da Oferta', value: offerName },
                { label: 'Mensagem do Order Bump', value: message },
                { label: 'Mensagem do Entregável', value: deliverableMessage },
                { label: 'Texto do botão aceitar', value: acceptText },
                { label: 'Texto do botão recusar', value: declineText },
            ]);
            if (!contentCheck.isValid) {
                showError(contentCheck.error);
                setLoading(false);
                return;
            }

            const payload = {
                name: offerName,
                message: message,
                deliverable_message: deliverableMessage,
                accept_button_text: acceptText,
                decline_button_text: declineText,
                price: Number(price),
                is_active: isActive,
                accept_button_color: acceptButtonColor,
                decline_button_color: declineButtonColor,
                media_send_mode: sendMode,
            };

            const { data } = await authService.order_bump.save_order_bump(botId, payload);

            if (!data.success) {
                throw new Error(data.message || "Erro ao salvar");
            }

            // 2️⃣ Pegar o ID (criar ou editar)
            const orderBumpId = data.offer_id || offerId;
            // console.log("Order Bump ID:", orderBumpId);



            // 4️⃣ Atualizar o state com o novo ID
            // setOfferId(orderBumpId);


            // 3️⃣ Upload direto com o ID
            if (mediaRef.current && orderBumpId) {
                await mediaRef.current.uploadAll(orderBumpId);
            }


            showSuccess("Order Bump salvo com sucesso!");

        } catch (err) {
            showError(err?.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* <header style={{ marginBottom: 24 }}>
                <h1 className='title'>Configuração de Order Bump</h1>
                <p>Configure a oferta adicional que será apresentada aos clientes durante o checkout.</p>
            </header> */}



            {loadingPage ? (
                <TabSkeleton tab="order_bump" />
            ) : (

                <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>

                    <div className="anti-clone-toggle">
                        <div className="anti-clone-info">
                            <span className="anti-clone-icon"><Gift size={16} /></span>
                            <span className="anti-clone-title">Ativar Order Bump</span>
                        </div>
                        <label className="toggle-wrapper">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                            <span className="toggle-slider" />
                        </label>
                    </div>



                    <div className='spt-form-bot'>
                        {/* Nome */}
                        <div className="mt-4">
                            <label className="spt-label">Nome da Oferta <span className="required">*</span></label>
                            <input
                                value={offerName}
                                onChange={(e) => setOfferName(e.target.value)}
                                required
                                className="input3"
                                placeholder="Ex: Bônus Exclusivo VIP "
                            />
                            <small className="form-hint">
                                Nome da oferta que será exibido ao usuário durante o checkout.
                            </small>
                        </div>



                        
                        {/* Media */}
                        <div className="mt-8">
                            <MediaManager
                                ref={mediaRef}
                                botId={botId}
                                targetType="offer"
                                targetId={offerId}
                                multiMedia={true}
                                mediaList={mediaList}
                                onMediaChange={setMediaList}
                                sendMode={sendMode}
                                onSendModeChange={setSendMode}
                                uploadContext="orderbump"
                            />
                        </div>

                        {/* Mensagem */}
                        <div className="mt-8">
                            <label className="spt-label">Mensagem do Order Bump <span className="required">*</span></label>
                            <TextForm 
                            value={message} 
                            placeholder="🎁 Digite o texto da mensagem do Order Bump aqui..."
                            onChange={setMessage} hint="Mensagem que será exibida ao cliente, apresentando a oferta adicional." 
                            showVariables={false} />

                        </div>

                        {/* Preço */}
                        <div className="mt-8">
                            <label className="spt-label">Preço {currencyLabel}<span className="text-white/60 text-sm ml-1">{currencySymbol}</span> <span className="required">*</span></label>
                            <input
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                className="input3"
                                min="2"
                                step="0.01"
                                required
                            />
                        </div>

                        {/* Botões */}
                        <div className="block md:flex w-full gap-8">
                            <div className="mt-8 w-full">
                                <label className="spt-label">Texto do botão recusar <span className="required">*</span></label>
                                <input
                                    value={declineText}
                                    onChange={(e) => setDeclineText(e.target.value)}
                                    className="input3"
                                    placeholder="🔴RECUSO🔴"
                                    required
                                />

                                <div className="mx-auto mt-4 w-max flex items-center gap-2 md:mx-0">
                                    {[
                                        { label: "Padrão", value: "default", class: "bg-gray-600" },
                                        { label: "Vermelho", value: "red", class: "bg-red-600" },
                                        { label: "Verde", value: "green", class: "bg-green-600" },
                                        { label: "Azul", value: "blue", class: "bg-blue-600" },
                                    ].map((colorOption) => (
                                        <button
                                            key={colorOption.value}
                                            type="button"
                                            title={colorOption.label}
                                            onClick={() =>
                                                setDeclineButtonColor(colorOption.value)
                                            }
                                            className={`w-5 h-5 rounded-full border-2 transition-all ${colorOption.class} ${declineButtonColor === colorOption.value
                                                ? "border-white scale-110"
                                                : "border-white/20"}`}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="mt-8 w-full">
                                <label className="spt-label">Texto do botão aceitar <span className="required">*</span></label>
                                <input
                                    value={acceptText}
                                    onChange={(e) => setAcceptText(e.target.value)}
                                    className="input3"
                                    placeholder="🟢ACEITO🟢"
                                    required
                                />

                                <div className="mx-auto mt-4 w-max flex items-center gap-2 md:mx-0">
                                    {[
                                        { label: "Padrão", value: "default", class: "bg-gray-600" },
                                        { label: "Vermelho", value: "red", class: "bg-red-600" },
                                        { label: "Verde", value: "green", class: "bg-green-600" },
                                        { label: "Azul", value: "blue", class: "bg-blue-600" },
                                    ].map((colorOption) => (
                                        <button
                                            key={colorOption.value}
                                            type="button"
                                            title={colorOption.label}
                                            onClick={() =>
                                                setAcceptButtonColor(colorOption.value)
                                            }
                                            className={`w-5 h-5 rounded-full border-2 transition-all ${colorOption.class} ${acceptButtonColor === colorOption.value
                                                ? "border-white scale-110"
                                                : "border-white/20"}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Mensagem Entregavel*/}
                        <div className="mt-8">
                            <label className="spt-label">Mensagem do Entregável <span className="required">*</span></label>
                            <textarea
                                rows={5}
                                value={deliverableMessage}
                                onChange={(e) => setDeliverableMessage(e.target.value)}
                                className='spt-textarea'
                                placeholder={"🎉Parabéns! Aqui está o seu bônus exclusivo:\n\n📌Link de acesso: https://t.me/+abcdEFG12345\n📌Aproveite!"}
                                required
                            />
                            <small className="form-hint">
                                Mensagem que será exibida ao cliente, apresentando a oferta adicional.
                            </small>
                        </div>

                        <div className="flex justify-end">
                            <button disabled={loading} className="btn-primary mt-8">
                                {loading ? "Salvando..." : "Salvar Order Bump"}
                            </button>
                        </div></div>
                </form>)}
        </>
    );
}
